import * as React from 'react';
import ReactDOM from 'react-dom';
import { useFiltersContext } from '../contexts/filters';
import { useSettingsContext } from '../contexts/settings';
import AriaHandler from '../../../../../src/js/components/AriaHandler';

interface IProps {
	children: React.ReactNode;
	onClose: () => void;
	isOpen: boolean;
}

const Modal = ({ children, onClose, isOpen }: IProps) => {
	const modalRef = React.useRef<HTMLDivElement>(null);
	const previouslyFocusedElement = React.useRef<HTMLElement | null>(null);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Escape') {
			onClose();
		}
	};

	React.useEffect(() => {
		if (isOpen && modalRef.current) {
			const ownerDocument = modalRef.current.ownerDocument;
			previouslyFocusedElement.current = ownerDocument.activeElement as HTMLElement;
			modalRef.current.focus();
			AriaHandler.trapFocus(modalRef.current);
		} else if (!isOpen && previouslyFocusedElement.current) {
			previouslyFocusedElement.current.focus();
		}
	}, [isOpen]);

	return ReactDOM.createPortal(
		<div className="pfa-modal">
			<div
				className="pfa-modal__overlay"
				onClick={onClose}
				onKeyDown={handleKeyDown}
				role="button"
				tabIndex={0}
			>
				<StopPropagationWrapper>
					<div
						className="pfa-modal__container"
						role="dialog"
						aria-modal="true"
						tabIndex={-1}
						ref={modalRef}
						aria-labelledby="modal-header"
						aria-describedby="modal-body"
					>
						<button
							className="pfa-modal__close btn"
							onClick={onClose}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									onClose();
								}
							}}
							tabIndex={0}
							aria-label="Close modal"
						>
							<i className="icon-x-mark" />
						</button>
						{children}
					</div>
				</StopPropagationWrapper>
			</div>
		</div>,
		document.body
	);
};

const StopPropagationWrapper = ({ children }: { children: React.ReactNode }) => {
	const handleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.stopPropagation();
		}
	};

	return (
		<div
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			role="button"
			tabIndex={0}
			aria-label="Stop propagation wrapper"
			className="pfa-modal__stop-propagation-wrapper"
		>
			{children}
		</div>
	);
};

Modal.displayName = 'Modal';

export default Modal;
